/**
 * @file carousel-landing.component.ts
 * @description
 * This component is responsible for rendering an auto-scrolling banner carousel
 * on the website's landing page. It enhances the visual appeal and provides dynamic
 * messaging through rotating banners.
 *
 * The component includes the following features:
 * 1. Automatically scrolls through a list of banner images or slides at regular intervals.
 * 2. Mobile and desktop responsive display using CSS or Angular breakpoints.
 * 3. Integrates with backend API to fetch dynamic banner content.
 * 4. Animations and transitions between slides for a smooth visual experience.
 *
 * @dependencies:
 * - Angular built-in features like *ngFor, *ngIf, and event binding.
 * - Third-party carousel/slider libraries ngx-owl-carousel-o
 * - Angular Services (for fetching banner content from API).
 *
 * @inputs (optional):
 * - `banners`: Array of banner objects
 *
 */

import { Component } from '@angular/core';
import { CarouselModule, OwlOptions } from 'ngx-owl-carousel-o';
import { BannerService } from '../../../shared/services/banners/banner.service';
import { CommonModule } from '@angular/common';
import { SharedModule } from '../../../shared/shared.module';
import { SeoService } from '../../../shared/services/seo.service';

@Component({
  selector: 'app-carousel-landing',
  templateUrl: './carousel-landing.component.html',
  styleUrls: ['./carousel-landing.component.scss'],
  standalone: true,
  imports: [
    CarouselModule,
    CommonModule,
    SharedModule
  ]
})

export class CarouselLandingComponent {

  public crossFadeOptions: OwlOptions = {
    loop: true,
    autoplay: true,
    autoplayTimeout: 3000,
    autoplayHoverPause: true,
    mouseDrag: false,
    touchDrag: true,
    pullDrag: true,
    dots: false,
    nav: false,
    navSpeed: 1000,
    // navText: ['<', '>'],
    margin: 0,
    responsive: {
      0: {
        items: 1,
      },
      // 600: {
      //   items: 2,
      // },
      // 1000: {
      //   items: 3,
      // }
    }
  };

  public crossFadeData: { img: string }[] = [];
  public crossFadeDataMobile: { img: string }[] = [];

  constructor(private _bannerService: BannerService, private seo: SeoService) {
    /**
       * Sets up SEO meta tags using SeoService
       * This helps in optimizing the page for search engines and social media previews.
   */
    this.seo.setTags({
      title: 'Carousel Banner component', // Title
      titleSuffix: '- Shining On Safety', // Title Suffix
      description: 'Shining On Safety', // Description
      image: '', // Image
      keywords: 'Shining On Safety' // Keywords
    });
  }

  ngOnInit(): void {
    this.getAllBanners();
  }

  /**
   * Get all banner api call
   */
  getAllBanners() {
    this._bannerService.getAllBanners().subscribe({
      next: (res) => {
        if (res?.data?.length) {
          const banners = res.data;

          this.crossFadeData = banners.map((banner: any) => ({
            img: banner.image
          }));

          // You can separate or filter for mobile if needed
          this.crossFadeDataMobile = this.crossFadeData;
        }
      },
      error: (error) => {
        console.error('Failed to load banners:', error);
      }
    });
  }
}
